import React from 'react';
import useNavigationState, { NavigationState } from './useNavigationState';
import { MOBILE_NAVIGATION_OPTIONS, MobileNavigationOptions } from '.';
import Text from '../Text/Text';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { twMerge } from 'tailwind-merge';
import { GridMenu } from '@drift-labs/icons';
import Button from '../Button';
import MobileHelpButton from '../Buttons/MobileHelpButton';
import CurrentRpcHealth from '../CurrentRpcHealth';
import PriorityFeeDisplay from '../PriorityFeeDisplay';

export const MobileNavigationMenuToggle = () => {
	const navigationState = useNavigationState();

	return (
		<Button.Secondary
			size="MEDIUM"
			onClick={() => {
				navigationState.setShowMobileNavMenu(
					!navigationState.showMobileNavMenu
				);
			}}
			iconOnly
			className="px-[4px] py-[4px] w-8"
		>
			<GridMenu width={20} height={20} color="var(--text-emphasis)" />
		</Button.Secondary>
	);
};

export const MobileNavigationMenuButton = ({
	handleNavigation,
	option,
	currentNavigationSelection,
}: {
	option: MobileNavigationOptions[0];
	handleNavigation: NavigationState['handleNavigation'];
	currentNavigationSelection: NavigationState['currentNavigationSelection'];
}) => {
	const Icon = option.icon;

	const handleClick = () => {
		handleNavigation(option.handler());
	};

	const isHighlighted = currentNavigationSelection === option.key;

	return (
		<div
			onClick={handleClick}
			className={twMerge(
				`flex flex-col items-center justify-center box-border w-full h-full hover:cursor-pointer hover:text-text-emphasis py-3 `,
				isHighlighted ? 'text-text-emphasis' : 'text-text-label'
			)}
		>
			{isHighlighted ? (
				<Icon size={32} color="url(#icon-gradient)" />
			) : (
				<Icon size={32} />
			)}
			<div className="mt-1 text-center">
				<Text.BODY2 className="">{option.name}</Text.BODY2>
			</div>
		</div>
	);
};

const SlideInAnimation = keyframes`
  0% {
    -webkit-transform: translateY(-100%);
            transform: translateY(-100%);
    opacity: 0.5;
  }
  100% {
    -webkit-transform: translateY(0);
            transform: translateY(0);
    opacity: 1;
  }
`;

export const SlideinMobileNavWrapper = styled.div`
	animation-name: ${SlideInAnimation};
	animation-duration: 150ms;
	animation-timing-function: ease-in;
	animation-direction: normal;
`;

export const MobileNavigationMenu = () => {
	const navigationState = useNavigationState();

	const handleBgClick = () => {
		navigationState.setShowMobileNavMenu(false);
	};

	return (
		<div
			id="mobile_nav_menu"
			className="absolute top-0 z-50 w-full h-full text-text-emphasis"
		>
			{/* Opaque Background */}
			<div
				className="absolute z-10 w-full h-full bg-black opacity-50"
				onClick={handleBgClick}
			/>
			{/* Content */}
			<SlideinMobileNavWrapper className="fixed z-20 flex flex-col w-full rounded-b-lg bg-container-bg">
				<div className="px-6 py-4">
					<div className="grid grid-cols-3 grid-rows-3">
						{MOBILE_NAVIGATION_OPTIONS.map((option) => {
							return (
								<MobileNavigationMenuButton
									handleNavigation={navigationState.handleNavigation}
									key={option.name}
									option={option}
									currentNavigationSelection={
										navigationState.currentNavigationSelection
									}
								/>
							);
						})}
					</div>
				</div>
				<div className="mx-[5%] content-center border-b border-container-border" />
				<div className="flex items-center px-2 pb-2 [&>div]:align-middle [&>div]:justify-center [&>div]:pl-2 [&>div]:flex-1 mt-2 divide-x divide-container-border">
					<div>
						<CurrentRpcHealth mobile />
					</div>

					<div>
						<PriorityFeeDisplay mobile showIcon />
					</div>

					<div>
						<MobileHelpButton />
					</div>
				</div>
			</SlideinMobileNavWrapper>
		</div>
	);
};
